import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { XClose, Download01 } from "@untitledui/icons";
import { Table } from "@/components/application/table/table";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { SectionHeader } from "@/components/application/section-headers/section-headers";
import { AppDialog } from "@/components/app/AppDialog";
import { Pager } from "@/components/app/Pager";
import { useAuditLog, auditExportUrl, type AuditEntry, type AuditFilter } from "@/lib/users";
import { useDebounce } from "@/hooks/use-debounce";
import { formatDateTime } from "@/lib/format";
import { AppSelect } from "@/components/app/AppSelect";

const inputCls =
  "rounded-lg bg-primary px-3 py-2 text-sm text-primary shadow-xs ring-1 ring-primary outline-none focus:ring-2 focus:ring-brand";
const REFRESH_OPTIONS = [0, 5000, 10000, 30000];

/** Skupiny event typů — CSV přesných hodnot (backend filtruje víc hodnot přes čárku). */
const EVENT_GROUPS: { labelKey: string; types: string }[] = [
  { labelKey: "audit.group.login", types: "login.success,login.failure,login.locked,login.directory_down,logout" },
  { labelKey: "audit.group.password", types: "password.changed,password.reset" },
  { labelKey: "audit.group.users", types: "user.created,user.updated,user.disabled,user.deleted,role.changed" },
  { labelKey: "audit.group.apiKeys", types: "api_key.created,api_key.used,api_key.revoked,api_client.deleted" },
  { labelKey: "audit.group.security", types: "jwt.key_rotated,tls.cert_renewed,directory.test_bind" },
];

function outcomeColor(o: string): "success" | "error" | "warning" | "gray" {
  const u = o?.toUpperCase();
  if (u === "SUCCESS") return "success";
  if (u === "FAILURE" || u === "DENIED") return "error";
  if (u === "WARNING") return "warning";
  return "gray";
}

/** Auth audit log — přihlášení, správa uživatelů, bezpečnostní události. */
export function AuditPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [refreshMs, setRefreshMs] = useState(0);
  const [usernameInput, setUsernameInput] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [actorInput, setActorInput] = useState("");
  const [ipInput, setIpInput] = useState("");
  const [targetInput, setTargetInput] = useState("");
  const [eventType, setEventType] = useState("");
  const [outcome, setOutcome] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [detail, setDetail] = useState<AuditEntry | null>(null);

  const username = useDebounce(usernameInput, 300);
  const search = useDebounce(searchInput, 300);
  const actor = useDebounce(actorInput, 300);
  const ipAddress = useDebounce(ipInput, 300);
  const target = useDebounce(targetInput, 300);

  const filter: AuditFilter = useMemo(
    () => ({ username, search, actor, ipAddress, target, eventType, outcome, from, to }),
    [username, search, actor, ipAddress, target, eventType, outcome, from, to],
  );
  const { data, isLoading, isError, isFetching } = useAuditLog(page, pageSize, filter, refreshMs);

  const hasFilters =
    usernameInput || searchInput || actorInput || ipInput || targetInput || eventType || outcome || from || to;
  const clear = () => {
    setUsernameInput(""); setSearchInput(""); setActorInput(""); setIpInput(""); setTargetInput("");
    setEventType(""); setOutcome(""); setFrom(""); setTo(""); setPage(0);
  };

  /** Server-side export — všechny odfiltrované řádky včetně user agenta a details. */
  const doExport = (fmt: "json" | "csv") => window.open(auditExportUrl(fmt, filter), "_blank");

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex-1">
            <SectionHeader.Heading>{t("audit.title")}</SectionHeader.Heading>
            <SectionHeader.Subheading>{t("audit.subtitle")}</SectionHeader.Subheading>
          </div>
          <SectionHeader.Actions>
            <Button size="sm" color="secondary" iconLeading={Download01} onPress={() => doExport("json")}>JSON</Button>
            <Button size="sm" color="secondary" iconLeading={Download01} onPress={() => doExport("csv")}>CSV</Button>
          </SectionHeader.Actions>
        </SectionHeader.Group>

        {/* Filtry jsou druhý potomek Rootu — patří k hlavičce, ne k obsahu
            (Root je `flex-col gap-5` právě na tenhle druhý řádek). */}
        <div className="flex flex-wrap items-end gap-3">
          <Filter label={t("tokens.col.username")}><input className={inputCls} value={usernameInput} onChange={(e) => { setUsernameInput(e.target.value); setPage(0); }} /></Filter>
          <Filter label={t("audit.event")}><input className={inputCls} value={eventType} onChange={(e) => { setEventType(e.target.value); setPage(0); }} /></Filter>
          <Filter label={t("audit.outcome")}>
            <AppSelect fullWidth={false} value={outcome} onChange={(e) => { setOutcome(e.target.value); setPage(0); }}>
              <option value="">{t("tokens.filter.all")}</option>
              <option value="SUCCESS">SUCCESS</option>
              <option value="FAILURE">FAILURE</option>
              <option value="DENIED">DENIED</option>
              <option value="WARNING">WARNING</option>
            </AppSelect>
          </Filter>
          <Filter label={t("audit.actor")}><input className={inputCls} value={actorInput} onChange={(e) => { setActorInput(e.target.value); setPage(0); }} /></Filter>
          <Filter label={t("audit.ip")}><input className={inputCls} value={ipInput} onChange={(e) => { setIpInput(e.target.value); setPage(0); }} /></Filter>
          <Filter label={t("audit.target")}><input className={inputCls} value={targetInput} onChange={(e) => { setTargetInput(e.target.value); setPage(0); }} /></Filter>
          <Filter label={t("txlogs.filter.from")}><input type="date" className={inputCls} value={from} onChange={(e) => { setFrom(e.target.value); setPage(0); }} /></Filter>
          <Filter label={t("txlogs.filter.to")}><input type="date" className={inputCls} value={to} onChange={(e) => { setTo(e.target.value); setPage(0); }} /></Filter>
          <Filter label={t("audit.search")}><input className={inputCls} value={searchInput} onChange={(e) => { setSearchInput(e.target.value); setPage(0); }} /></Filter>
          <Filter label={t("pager.pageSize")}>
            <AppSelect fullWidth={false} value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}>
              {[20, 50, 100].map((s) => <option key={s} value={s}>{s}</option>)}
            </AppSelect>
          </Filter>
          <Filter label={t("txlogs.filter.autoRefresh")}>
            <AppSelect fullWidth={false} value={refreshMs} onChange={(e) => setRefreshMs(parseInt(e.target.value, 10))}>
              {REFRESH_OPTIONS.map((ms) => <option key={ms} value={ms}>{ms === 0 ? t("txlogs.filter.off") : `${ms / 1000}s`}</option>)}
            </AppSelect>
          </Filter>
          {hasFilters && (
            <Button color="secondary" size="md" iconLeading={XClose} onPress={clear}>{t("tokens.filter.clear")}</Button>
          )}
        </div>
      </SectionHeader.Root>

      {/* Rychlé skupinové chipy pro event type (CSV přesných hodnot) */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-tertiary">{t("audit.eventGroups")}:</span>
        {EVENT_GROUPS.map((g) => (
          <button
            key={g.labelKey}
            type="button"
            onClick={() => { setEventType(g.types); setPage(0); }}
            className={`rounded-full px-2.5 py-1 text-xs ring-1 ${eventType === g.types ? "bg-brand-solid text-white ring-brand" : "bg-primary text-secondary ring-secondary hover:bg-secondary"}`}
          >
            {t(g.labelKey)}
          </button>
        ))}
      </div>

      {isLoading && <LoadingIndicator />}
      {isError && <p className="text-sm text-error-primary">{t("common.loadError")}</p>}

      {data && (
        <>
          <div className="overflow-x-auto rounded-xl ring-1 ring-secondary">
            <Table aria-label={t("audit.title")} selectionMode="none">
              <Table.Header>
                <Table.Head isRowHeader id="timestamp" label={t("audit.time")} />
                <Table.Head id="eventType" label={t("audit.event")} />
                <Table.Head id="outcome" label={t("audit.outcome")} />
                <Table.Head id="username" label={t("tokens.col.username")} />
                <Table.Head id="actor" label={t("audit.actor")} />
                <Table.Head id="ipAddress" label={t("audit.ip")} />
                <Table.Head id="target" label={t("audit.target")} />
              </Table.Header>
              <Table.Body items={data.content}>
                {(row) => (
                  <Table.Row
                    id={String(row.id)}
                    onAction={() => setDetail(row)}
                    className={`cursor-pointer ${outcomeColor(row.outcome) === "error" ? "bg-error-primary/5" : ""}`}
                  >
                    <Table.Cell>{formatDateTime(row.timestamp, { seconds: true })}</Table.Cell>
                    <Table.Cell className="font-medium text-primary">{row.eventType}</Table.Cell>
                    <Table.Cell>
                      <Badge type="pill-color" size="sm" color={outcomeColor(row.outcome)}>{row.outcome}</Badge>
                    </Table.Cell>
                    <Table.Cell>{row.username ?? "—"}</Table.Cell>
                    <Table.Cell>{row.actor ?? "—"}</Table.Cell>
                    <Table.Cell>{row.ipAddress ?? "—"}</Table.Cell>
                    <Table.Cell>{row.target ?? "—"}</Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </div>

          <Pager page={data} onPage={setPage} isFetching={isFetching} />
        </>
      )}

      {detail && <AuditDetailDialog entry={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function Filter({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-tertiary">{label}</span>
      {children}
    </label>
  );
}

function AuditDetailDialog({ entry, onClose }: { entry: AuditEntry; onClose: () => void }) {
  const { t } = useTranslation();
  const details = (entry.details ?? {}) as Record<string, unknown>;
  const detailsJson = JSON.stringify(details, null, 2);
  return (
    <AppDialog
      isOpen
      onClose={onClose}
      size="2xl"
      title={t("audit.detailTitle")}
      footer={<Button color="secondary" onPress={onClose}>{t("common.close")}</Button>}
    >
      <div className="flex flex-col gap-2 text-sm">
        <Row label={t("audit.time")}>{formatDateTime(entry.timestamp, { seconds: true })}</Row>
        <Row label={t("audit.event")}>{entry.eventType}</Row>
        <Row label={t("audit.outcome")}>{entry.outcome}</Row>
        <Row label={t("tokens.col.username")}>{entry.username ?? "—"}</Row>
        <Row label={t("audit.actor")}>{entry.actor ?? "—"}</Row>
        <Row label={t("audit.ip")}>{entry.ipAddress ?? "—"}</Row>
        <Row label={t("audit.target")}>{entry.target ?? "—"}</Row>
        {details.userId != null && <Row label="User ID">{String(details.userId)}</Row>}
        {details.userAgent != null && <Row label="User-Agent">{String(details.userAgent)}</Row>}
        <div>
          <div className="mb-1 flex items-center justify-between">
            <span className="text-xs font-semibold text-tertiary">{t("audit.details")}</span>
            <Button size="sm" color="tertiary" onPress={() => void navigator.clipboard?.writeText(detailsJson)}>{t("audit.copy")}</Button>
          </div>
          <pre className="max-h-[50vh] overflow-auto rounded-md bg-secondary p-2 font-mono text-xs text-secondary ring-1 ring-secondary">{detailsJson}</pre>
        </div>
      </div>
    </AppDialog>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 border-b border-secondary py-1.5">
      <span className="text-tertiary">{label}</span>
      <span className="break-all text-primary">{children}</span>
    </div>
  );
}
